<template>
  <ion-app :class="{ 'has-supervision-banner': hasSupervisionBanner }">
    <SupervisionBanner />
    <AvisoAppModal />
    <AppVersionOutdatedModal />
    <ion-loading :is-open="mainStoreInstance.$state.loading.show" :message="mainStoreInstance.$state.loading.message"> </ion-loading>
  
    <ion-menu content-id="main-content">
      <ion-header>
        <ion-toolbar>
          <ion-title>Menu</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" style="--padding-bottom: max(60px, calc(env(safe-area-inset-bottom) + 60px));">
        <ion-list>
          <ion-item button @click="openHome">
            <ion-icon slot="start" :icon="homeOutline"></ion-icon>
            Inicio
          </ion-item>

          <ion-item 
                    button 
                    @click="openSesiones" 
                    v-if="userAuth().hasRole('coordinador de actividad') || userAuth().hasRole('profesor')"
                    >
            <ion-icon slot="start" :icon="schoolOutline"></ion-icon>
            Clases
          </ion-item>

          <ion-item button @click="openEventos">
            <ion-icon slot="start" :icon="calendarOutline"></ion-icon>
            Eventos
          </ion-item>

          <ion-item 
                    button 
                    @click="openQRScanner"
                    v-if="userAuth().hasRole('coordinador de actividad')"
                    >
            <ion-icon slot="start" :icon="qrCodeOutline"></ion-icon>
            Lector QR
          </ion-item>

          <ion-item button @click="openMensajes">
            <ion-icon slot="start" :icon="chatbubblesOutline"></ion-icon>
            Mensajes
          </ion-item>

          <ion-item button @click="openAlquileres">
            <ion-icon slot="start" :icon="businessOutline"></ion-icon>
            Alquileres
          </ion-item>

          <ion-item button @click="openSaldo">
            <ion-icon slot="start" :icon="walletOutline"></ion-icon>
            Mi Saldo
          </ion-item>

          <ion-item
                    button
                    @click="openContarAsistentes"
                    v-if="userAuth().hasRole('coordinador de actividad')"
                    >
            <ion-icon slot="start" :icon="eyeOutline"></ion-icon>
            Contar personas
          </ion-item>

          <ion-item button @click="openIncidencias">
            <ion-icon slot="start" :icon="warningOutline"></ion-icon>
            Incidencias
          </ion-item>

          <ion-item 
                      button 
                      @click="openWhatsappLauncher"
                      v-if="userAuth().can('Buscar alumnos para Whatsapp Launcher')"
                      >
            Lanzador de Whatsapps
          </ion-item>

          <ion-item 
                      button 
                      @click="openDeteccionNecesidades"
                      v-if="userAuth().can('Añadir deteccion de necesidades')"
                      >
            Detección de necesidades
          </ion-item>

          <ion-item 
                    button 
                    @click="openEntregas"
                    v-if="userAuth().hasRole('coordinador de actividad')"
                    >
            <ion-icon slot="start" :icon="cubeOutline"></ion-icon>
            Entregas
          </ion-item>

          <ion-item 
                    button 
                    @click="openRecaudacion"
                    v-if="userAuth().hasRole('coordinador de actividad')"
                    >
            <ion-icon slot="start" :icon="cashOutline"></ion-icon>
            Recaudación
          </ion-item>

          <ion-item button @click="openDomotica">
            <ion-icon slot="start" :icon="flashOutline"></ion-icon>
            Domótica
          </ion-item>

          <ion-item button @click="openLiquidacion">
            <ion-icon slot="start" :icon="documentTextOutline"></ion-icon>
            Liquidación
          </ion-item>
        </ion-list>
        <div style="text-align: center; position: absolute; bottom: 0; width: 100%; padding-bottom: max(20px, env(safe-area-inset-bottom)); left: 0; right: 0;">
          <ion-note style="font-size: smaller;">
            Versión {{ require('../package.json').version }}
          </ion-note>
          <div style="display: flex; align-items: center; justify-content: center; margin-top: 4px;">
            <ion-note>Hola, {{ tools().getShortName( currentUserStore().user?.name || '') }}</ion-note>
            <a href="#" @click.prevent="logout" style="margin-left: 12px; color: var(--ion-color-danger); text-decoration: none; display: inline-flex; align-items: center;">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
            </a>
        </div>
        </div>
      </ion-content>
    </ion-menu>
    
    <ion-router-outlet id="main-content" />

    <ion-button
      class="home-shortcut-btn"
      fill="solid"
      shape="round"
      color="light"
      @click="openHome"
    >
      <ion-icon :icon="homeOutline"></ion-icon>
    </ion-button>
    
    <!-- Floating Message Indicator -->
    <FloatingMessageIndicator />
  </ion-app>
</template>

<script lang="ts" setup>
import { mainStore } from '@/stores/mainStore';
import { IonApp, IonLoading, IonMenu, IonHeader, IonToolbar, IonTitle, IonContent, IonNote, IonRouterOutlet, IonItem, IonList, IonButton, IonIcon, menuController } from '@ionic/vue';
import tools from '@/composables/tools';
import { alquilerUiLog } from '@/composables/alquilerUiLog';
import router from '@/router';
import { currentUserStore } from '@/stores/currentUser'
import userAuth from './composables/userAuth';
import FloatingMessageIndicator from '@/components/FloatingMessageIndicator.vue';
import SupervisionBanner from '@/components/SupervisionBanner.vue';
import AvisoAppModal from '@/components/AvisoAppModal.vue';
import AppVersionOutdatedModal from '@/components/AppVersionOutdatedModal.vue';
import { useAvisoApp } from '@/composables/useAvisoApp';
import { configureNativeStatusBar } from '@/composables/useNativeStatusBar';
import { onMounted, ref } from 'vue';
import { trackMenuAccess } from '@/composables/menuUsage';
import {
  homeOutline,
  schoolOutline,
  calendarOutline,
  qrCodeOutline,
  chatbubblesOutline,
  businessOutline,
  walletOutline,
  warningOutline,
  cubeOutline,
  cashOutline,
  flashOutline,
  documentTextOutline,
  eyeOutline,
} from 'ionicons/icons';

const mainStoreInstance = mainStore();
const avisoApp = useAvisoApp();
const hasSupervisionBanner = ref(!!localStorage.getItem('supervision'));

onMounted(() => {
  void configureNativeStatusBar();
  if (localStorage.getItem('token')) {
    avisoApp.startPolling();
  }
});

const closeMenu = async() => {
      await menuController.toggle();
}

const openSesiones = () => {
  tools().setLog('Abre lista de sesiones desde el menú');
  trackMenuAccess('clases');
  router.push({ name: 'Sesiones'})
  closeMenu();
};

const openHome = () => {
  tools().setLog('Abre inicio desde el menú');
  location.href = '/inicio';
  closeMenu();
};

const logout = async () => {
  await closeMenu();
  await userAuth().logout();
};

const openAlquileres = () => {
  alquilerUiLog('menu_abre_alquileres')
  trackMenuAccess('alquileres');
  location.href = '/alquileres'; //me da error si no lo hago asi.. .tengo q solucionarlo
};

const openSaldo = () => {
  tools().setLog('Abre la sección de saldo desde el menú');
  trackMenuAccess('mi_saldo');
  location.href = '/saldo';
  closeMenu();
};

const openEventos = () => {
  tools().setLog('Abre lista de eventos desde el menú');
  trackMenuAccess('eventos');
  location.href = '/eventos'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Eventos' });
  closeMenu();
};

const openMensajes = () => {
  tools().setLog('Abre lista de mensajes desde el menú');
  trackMenuAccess('mensajes');
  location.href = '/mensajes'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Eventos' });
  closeMenu();
};

const openIncidencias = () => {
  tools().setLog('Abre lista de incidencias desde el menú');
  trackMenuAccess('incidencias');
  location.href = '/Organizacion/incidencias'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Eventos' });
  closeMenu();
};

const openContarAsistentes = () => {
  tools().setLog('Abre contaje aleatorio de personas en sesión');
  trackMenuAccess('contar_personas');
  location.href = '/contar-asistentes';
  closeMenu();
};

const openQRScanner = () => {
  tools().setLog('Abre el escanner QR desde el menú');
  trackMenuAccess('lector_qr');
  location.href = 'https://escaner.gest.galidancia.com'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Eventos' });
  closeMenu();
};

const openDeteccionNecesidades = () => {
  tools().setLog('Abre formulario de detección de necesidades');
  location.href = '/necesidades'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Eventos' });
  closeMenu();
};

const openWhatsappLauncher = () => {
  tools().setLog('Abre el whatsapp launcher');
  location.href = '/whatsappLauncher'; //me da error si no lo hago asi.. .tengo q solucionarlo
  //router.push({ name: 'Whatsapp Launcher' });
  closeMenu();
};

const openEntregas = () => {
  tools().setLog('Abre lista de entregas desde el menú');
  trackMenuAccess('entrega');
  router.push('/entrega');
  closeMenu();
};

const openDomotica = () => {
  tools().setLog('Abre la sección de domótica desde el menú');
  trackMenuAccess('domotica');
  location.href = '/domotica';
  closeMenu();
};

const openLiquidacion = () => {
  tools().setLog('Abre la sección de liquidación desde el menú');
  trackMenuAccess('liquidacion');
  location.href = '/liquidacion';
  closeMenu();
};

const openRecaudacion = () => {
  tools().setLog('Abre la sección de recaudación desde el menú');
  trackMenuAccess('recaudacion');
  location.href = '/recaudacion';
  closeMenu();
};
</script>

<style scoped>
.home-shortcut-btn {
  position: fixed;
  top: calc(env(safe-area-inset-top) + 8px);
  right: 12px;
  z-index: 1200;
  --padding-start: 10px;
  --padding-end: 10px;
  --box-shadow: 0 8px 18px rgba(15, 23, 42, 0.2);
}

.home-shortcut-btn ion-icon {
  font-size: 1.2rem;
}
</style>

<style>
ion-app.has-supervision-banner {
  --galidancia-gest-supervision-banner-offset: calc(env(safe-area-inset-top, 0px) + 88px);
}

ion-app.has-supervision-banner ion-page ion-header {
  margin-top: var(--galidancia-gest-supervision-banner-offset);
}

ion-app.has-supervision-banner .home-shortcut-btn {
  top: calc(var(--galidancia-gest-supervision-banner-offset) + 8px);
}
</style>
